import { useMutation, useQuery } from '@apollo/client';
import { Box, Grid, styled } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import EmptyGridStateIcon from 'assets/img/empty-grid-state.svg';
import DataImportOverviewCard from 'FeatureModules/DataImport/Components/DataImportOverviewCard';
import { DATA_IMPORT_CATEGORY_CONFIG } from 'FeatureModules/DataImport/utils/dataImportConfig';
import FlexBox from 'Components/FlexBox';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { DELETE_DATA_IMPORT } from 'graphql/secure/mutations/import.mutations';
import { GET_FIRST_MILE_IMPORTS } from 'graphql/secure/queries/import.queries';
import React, { FC, useState } from 'react';
import Moment from 'react-moment';
import { useNavigate, useParams } from 'react-router-dom';
import { Booleanish } from 'types/booleanish.types';
import {
  TFirstMileImport,
  IFirstMileImportsResponse,
} from 'FeatureModules/DataImport/types/dataImport.types';
import {
  DataItem,
  DetailsCard,
  DatasetDate,
  DatasetTitle,
} from 'FeatureModules/DataImport/Components/DataImport.styles';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import ActionContainer from 'Components/ActionContainer';
import PageSubTitle from 'Components/PageSubTitle';

const Container = styled('div')(() => ({
  position: 'relative',
  minHeight: 50,
}));

const DatasetContainer = styled(Box)(() => ({
  flexWrap: 'wrap',
  display: 'flex',
}));

const ImageContainer = styled(FlexBox)(() => ({
  background: '#fff',
  borderRadius: 8,
  padding: '16px',
  marginBottom: 8,
}));

const FarmImport: FC = () => {
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const { id: companyId } = useParams<{ id?: string }>();
  const { company } = useCompanyPlanContext();

  const [importDetails, setImportDetails] = useState<TFirstMileImport>();

  const { data, loading } = useQuery<IFirstMileImportsResponse>(GET_FIRST_MILE_IMPORTS, {
    variables: { companyId: companyId || company.id },
  });

  const [deleteDataImport] = useMutation(DELETE_DATA_IMPORT, {
    onCompleted: () => {
      setSuccessMessage('Data import successfully deleted');
    },
    onError: () => {
      setErrorMessage('Failed to delete data import');
    },
    refetchQueries: [GET_FIRST_MILE_IMPORTS],
  });

  const categoryConfig = DATA_IMPORT_CATEGORY_CONFIG[0];

  const startEditingTable = (importId?: string) => {
    const basePath = companyId
      ? `/partners/${companyId}/imports/${categoryConfig.key}`
      : `/imports/${categoryConfig.key}`;
    navigate(`${basePath}${importId ? `/${importId}` : ''}`);
  };

  const handleDeleteImport = async (id: string) => {
    await deleteDataImport({ variables: { id } });
    setImportDetails(undefined);
  };

  const handleDeleteImportClick = () => {
    if (!importDetails) {
      // eslint-disable-next-line no-console
      console.log('No import details to delete');
      return;
    }
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete data import',
        itemTitle: importDetails.title || 'Unnamed',
        text: 'Are you sure you want to delete this data import? The imported data will still be available in the system.',
        cancelText: 'Cancel',
        onSubmit: () => handleDeleteImport(importDetails.id),
        onCancel: () => undefined,
      },
    });
  };

  return (
    <Container>
      {!loading && data && data?.firstMileImports.edges.length > 0 ? (
        <>
          <ActionContainer>
            <ThemeButton
              size="medium"
              color="PRIMARY"
              startIcon={<Plus size={20} color="white" />}
              onClick={() => startEditingTable()}
            >
              Add new data set
            </ThemeButton>
          </ActionContainer>

          <Box mt={4}>
            <Box gap={2} display="flex" justifyContent="space-between">
              <DatasetContainer gap={2}>
                {data.firstMileImports.edges.map(({ node }) => (
                  <Grid item key={node.id} xs={6} sm={4} md={3} lg={2}>
                    <DataItem
                      selected={Booleanish(importDetails?.id === node.id)}
                      onClick={() => setImportDetails(node)}
                    >
                      <ImageContainer justifyContent="center" mb={2}>
                        <img width={100} src={EmptyGridStateIcon} alt="empty" />
                      </ImageContainer>
                      {node.title ? (
                        <DatasetTitle variant="BODY_MEDIUM_BOLD" autoOverflow>
                          {node.title}
                        </DatasetTitle>
                      ) : (
                        <DatasetTitle variant="BODY_MEDIUM_BOLD" unnamed autoOverflow>
                          Unnamed
                        </DatasetTitle>
                      )}
                      <DatasetDate>
                        <Moment format="L">{node.createdTimestamp}</Moment>
                      </DatasetDate>
                    </DataItem>
                  </Grid>
                ))}
              </DatasetContainer>

              {importDetails !== undefined && (
                <Box position="relative" minWidth={395} width="20%">
                  <DetailsCard>
                    <PageSubTitle title="Details" />
                    <Box mb={1} />
                    <ThemeTypography variant="BODY_MEDIUM_BOLD">
                      {importDetails.title || 'Unnamed'}
                    </ThemeTypography>
                    <FlexBox mb={2}>
                      <ThemeTypography variant="BODY_MEDIUM">
                        Created <Moment format="LL">{importDetails.createdTimestamp}</Moment>
                      </ThemeTypography>
                    </FlexBox>
                    <FlexBox gap={2}>
                      <ThemeButton
                        color="BLUE_ICE"
                        startIcon={<Icon name="show" />}
                        onClick={() => startEditingTable(importDetails?.id)}
                      >
                        View
                      </ThemeButton>
                      <ThemeButton
                        color="BLUE_ICE"
                        startIcon={<Icon name="delete" />}
                        onClick={handleDeleteImportClick}
                      >
                        Delete
                      </ThemeButton>
                    </FlexBox>
                  </DetailsCard>
                </Box>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <DataImportOverviewCard
          title={`Add your ${categoryConfig.dataLabel} data to enable tracing of impact along your supply chains`}
          onAddTableClick={startEditingTable}
        />
      )}
    </Container>
  );
};

export default FarmImport;
